import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';
import ConversationsTab from './components/ConversationsTab';

// ============================================================
// Chat Live — configuration du marchand.
// Active/désactive le chat sur le site public, personnalise l'agent
// (nom, message d'accueil, couleur) et prévisualise le rendu.
// La config est stockée dans `chat_live_config` (isolée par commerce).
// ============================================================

interface ConfigRow {
  enabled: boolean;
  agent_name: string;
  welcome_message: string;
  primary_color: string;
  position: string;
  show_products: boolean;
  quick_questions: string[];
}

const DEFAULT_CONFIG: ConfigRow = {
  enabled: false,
  agent_name: 'Assistant',
  welcome_message: 'Bonjour ! Comment puis-je vous aider ?',
  primary_color: '#1f2937',
  position: 'right',
  show_products: true,
  quick_questions: ['Quels sont vos prix ?', 'Quels produits avez-vous ?', 'Voir mon panier'],
};

const COLOR_PRESETS = ['#1f2937', '#b45309', '#0f766e', '#7c2d12', '#4d7c0f', '#9d174d'];

export default function ChatLivePage() {
  const { user } = useAuth();
  const [config, setConfig] = useState<ConfigRow>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [newQuestion, setNewQuestion] = useState('');
  const [tab, setTab] = useState<'config' | 'conversations'>('config');

  const fetchConfig = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await supabase
        .from('chat_live_config')
        .select('enabled, agent_name, welcome_message, primary_color, position, show_products, quick_questions')
        .eq('idcommerce', getCommerceId(user))
        .maybeSingle();
      if (data) {
        setConfig({
          enabled: !!data.enabled,
          agent_name: data.agent_name || 'Assistant',
          welcome_message: data.welcome_message || DEFAULT_CONFIG.welcome_message,
          primary_color: data.primary_color || '#1f2937',
          position: data.position || 'right',
          show_products: data.show_products !== false,
          quick_questions: Array.isArray(data.quick_questions) ? data.quick_questions : [],
        });
      }
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    setMsg(null);
    try {
      const { error } = await supabase.from('chat_live_config').upsert({
        idcommerce: getCommerceId(user),
        enabled: config.enabled,
        agent_name: config.agent_name.trim() || 'Assistant',
        welcome_message: config.welcome_message.trim() || DEFAULT_CONFIG.welcome_message,
        primary_color: config.primary_color,
        position: config.position,
        show_products: config.show_products,
        quick_questions: config.quick_questions,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'idcommerce' });
      if (error) throw error;
      setMsg({ type: 'success', text: config.enabled ? 'Chat Live enregistré et activé sur votre site.' : 'Chat Live enregistré (désactivé).' });
    } catch (err: unknown) {
      setMsg({ type: 'error', text: err instanceof Error ? err.message : 'Erreur lors de l’enregistrement' });
    } finally {
      setSaving(false);
    }
  };

  const addQuestion = () => {
    const q = newQuestion.trim();
    if (!q || config.quick_questions.includes(q)) return;
    setConfig((c) => ({ ...c, quick_questions: [...c.quick_questions, q] }));
    setNewQuestion('');
  };

  const removeQuestion = (q: string) => {
    setConfig((c) => ({ ...c, quick_questions: c.quick_questions.filter((x) => x !== q) }));
  };

  if (loading) {
    return <div className="p-4 md:p-6 flex items-center justify-center py-20"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>;
  }

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
            <i className="ri-chat-smile-2-line mr-2 text-primary-500"></i>Chat Live
          </h2>
          <p className="text-sm text-foreground-500 mt-1">Un assistant IA sur votre site qui renseigne les visiteurs et les aide à acheter</p>
        </div>
        {tab === 'config' && (
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer"
        >
          {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
          Enregistrer
        </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-0.5 bg-background-100 rounded-full p-1 w-fit mb-6">
        <button
          onClick={() => setTab('config')}
          className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${tab === 'config' ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}
        >
          Configuration
        </button>
        <button
          onClick={() => setTab('conversations')}
          className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${tab === 'conversations' ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}
        >
          Conversations
        </button>
      </div>

      {msg && (
        <div className={`mb-5 px-4 py-3 rounded-lg text-sm font-medium ${msg.type === 'success' ? 'bg-accent-50 text-accent-700' : 'bg-red-50 text-red-600'}`}>
          {msg.text}
        </div>
      )}

      {tab === 'config' && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Configuration */}
        <div className="space-y-5">
          {/* Activation */}
          <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <p className="text-sm font-semibold text-foreground-900">Activer le Chat Live</p>
                <p className="text-xs text-foreground-500 mt-0.5">Affiche la bulle de chat sur toutes les pages de votre site</p>
              </div>
              <span className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${config.enabled ? 'bg-accent-500' : 'bg-background-200'}`}>
                <input type="checkbox" checked={config.enabled} onChange={(e) => setConfig((c) => ({ ...c, enabled: e.target.checked }))} className="sr-only" />
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${config.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
              </span>
            </label>
          </div>

          <div className="bg-background-50 border border-background-200/70 rounded-xl p-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Nom de l’assistant</label>
              <input type="text" value={config.agent_name} onChange={(e) => setConfig((c) => ({ ...c, agent_name: e.target.value }))}
                className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors" />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Message d’accueil</label>
              <textarea value={config.welcome_message} onChange={(e) => setConfig((c) => ({ ...c, welcome_message: e.target.value }))} rows={2}
                className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors resize-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-2">Couleur</label>
              <div className="flex items-center gap-2 flex-wrap">
                {COLOR_PRESETS.map((c) => (
                  <button
                    key={c}
                    onClick={() => setConfig((prev) => ({ ...prev, primary_color: c }))}
                    style={{ backgroundColor: c }}
                    className={`w-8 h-8 rounded-full cursor-pointer transition-transform ${config.primary_color === c ? 'ring-2 ring-offset-2 ring-foreground-400 scale-110' : 'hover:scale-105'}`}
                    aria-label={`Couleur ${c}`}
                  />
                ))}
              </div>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={config.show_products} onChange={(e) => setConfig((c) => ({ ...c, show_products: e.target.checked }))}
                className="w-4 h-4 rounded border-background-300/60 text-primary-500 focus:ring-primary-400 cursor-pointer" />
              <span className="text-sm text-foreground-700">Afficher les produits recommandés dans le chat</span>
            </label>
          </div>

          {/* Questions rapides */}
          <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
            <p className="text-sm font-semibold text-foreground-900 mb-3">Questions rapides</p>
            <div className="flex items-center gap-2 mb-3">
              <input type="text" value={newQuestion} onChange={(e) => setNewQuestion(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addQuestion(); } }}
                placeholder="Ex : Livrez-vous à Casablanca ?"
                className="flex-1 px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300" />
              <button onClick={addQuestion} className="px-3 py-2 bg-secondary-50 text-secondary-700 rounded-full text-sm font-medium whitespace-nowrap hover:bg-secondary-100 transition-colors cursor-pointer">
                <i className="ri-add-line"></i>
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {config.quick_questions.length === 0 ? (
                <p className="text-xs text-foreground-400">Aucune question rapide.</p>
              ) : config.quick_questions.map((q) => (
                <span key={q} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-background-100 rounded-full text-xs text-foreground-700">
                  {q}
                  <button onClick={() => removeQuestion(q)} className="text-foreground-400 hover:text-red-500 cursor-pointer"><i className="ri-close-line"></i></button>
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Aperçu */}
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
          <p className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2"><i className="ri-eye-line text-accent-500"></i>Aperçu</p>
          <div className="relative h-[440px] rounded-lg overflow-hidden bg-background-100 border border-background-200/70">
            <div className="absolute inset-0 flex flex-col items-center justify-center text-foreground-300 gap-2">
              <i className="ri-store-2-line text-4xl"></i>
              <span className="text-xs">Votre site public</span>
            </div>
            {/* Bulle */}
            <div className="absolute bottom-4 right-4 w-12 h-12 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: config.primary_color }}>
              <i className="ri-customer-service-2-line text-xl"></i>
            </div>
            {/* Aperçu panneau */}
            <div className="absolute bottom-20 right-4 w-64 rounded-xl overflow-hidden border border-background-200/70 bg-background-50 shadow-sm">
              <div className="px-3 py-2 text-white flex items-center gap-2" style={{ backgroundColor: config.primary_color }}>
                <i className="ri-robot-2-line text-sm"></i>
                <span className="text-xs font-semibold">{config.agent_name || 'Assistant'}</span>
              </div>
              <div className="p-3 space-y-2">
                <div className="bg-background-100 rounded-lg px-2.5 py-2 text-[11px] text-foreground-700">{config.welcome_message || DEFAULT_CONFIG.welcome_message}</div>
                <div className="flex flex-wrap gap-1">
                  {config.quick_questions.slice(0, 3).map((q) => (
                    <span key={q} className="px-2 py-0.5 rounded-full bg-secondary-50 text-secondary-700 text-[10px]">{q}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <p className="text-xs text-foreground-400 mt-3">
            <i className="ri-information-line mr-1"></i>
            Le chat analyse le catalogue de votre boutique : les visiteurs peuvent demander les prix, découvrir les produits, ajouter au panier et commander.
          </p>
        </div>
      </div>)}

      {tab === 'conversations' && <ConversationsTab />}
    </div>
  );
}